import cx from 'classix';
import { type EmblaCarouselType } from 'embla-carousel';
import {
  type ComponentPropsWithRef,
  type MouseEvent,
} from 'react';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import styles from './listing-carousel-arrows.module.scss';

interface UsePreviousNextButtonsType {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: (event: MouseEvent<HTMLButtonElement> | KeyboardEvent) => void;
  onNextButtonClick: (event: MouseEvent<HTMLButtonElement> | KeyboardEvent) => void;
}

export const useCarouselArrows = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (emblaApi: EmblaCarouselType) => void,
): UsePreviousNextButtonsType => {
  const [previousButtonDisabled,
    setPreviousButtonDisabled] = useState(true);
  const [nextButtonDisabled,
    setNextButtonDisabled] = useState(true);

  const onPreviousButtonClick = useCallback((event: MouseEvent<HTMLButtonElement> | KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!emblaApi) return;

    emblaApi.scrollPrev();

    if (onButtonClick) onButtonClick(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onNextButtonClick = useCallback((event: MouseEvent<HTMLButtonElement> | KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!emblaApi) return;

    emblaApi.scrollNext();

    if (onButtonClick) onButtonClick(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setPreviousButtonDisabled(!api.canScrollPrev());
    setNextButtonDisabled(!api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  return {
    nextBtnDisabled: nextButtonDisabled,
    onNextButtonClick,
    onPrevButtonClick: onPreviousButtonClick,
    prevBtnDisabled: previousButtonDisabled,
  };
};

type PropertyType = ComponentPropsWithRef<'button'>;
interface CarouselArrowsProps extends PropertyType {
  isMapPreview?: boolean;
  isListingDetailPreview?: boolean;
  isAccountPreview?: boolean;
  shade?: 'light' | 'dark';
}

export const PrevButton: React.FC<CarouselArrowsProps> = (props) => {
  const {
    children,
    isAccountPreview = false,
    isListingDetailPreview = false,
    isMapPreview = false,
    shade = 'light',
    ...restProps
  } = props;

  return (
    <button
      className={cx(
        styles.embla__button,
        styles.embla__button__left,
        isMapPreview && styles.embla__button__isMapPreview,
        isListingDetailPreview && styles.embla__button__isListingDetailPreview,
        isAccountPreview && styles.embla__button__isAccountPreview,
      )}
      type="button"
      aria-label="previous slide"
      {...restProps}
    >
      <svg
        className={cx(
          styles.embla__button__svg,
          styles[`embla__button__${shade}`],
        )}
        viewBox="0 0 532 532"
      >
        <path
          fill="currentColor"
          d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z"
        />
      </svg>
      {children}
    </button>
  );
};

export const NextButton: React.FC<CarouselArrowsProps> = (props) => {
  const {
    children,
    isAccountPreview = false,
    isListingDetailPreview = false,
    isMapPreview = false,
    shade = 'light',
    ...restProps
  } = props;

  return (
    <button
      className={cx(
        styles.embla__button,
        styles.embla__button__right,
        isMapPreview && styles.embla__button__isMapPreview,
        isListingDetailPreview && styles.embla__button__isListingDetailPreview,
        isAccountPreview && styles.embla__button__isAccountPreview,
      )}
      type="button"
      aria-label="next slide"
      {...restProps}
    >
      <svg
        className={cx(
          styles.embla__button__svg,
          styles.embla__button__svg__right,
          styles[`embla__button__${shade}`],
        )}
        viewBox="0 0 532 532"
      >
        <path
          fill="currentColor"
          d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2-45.865 45.901-206.404 206.564-229.332 229.52Z"
        />
      </svg>
      {children}
    </button>
  );
};
